import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReqError from "@source/api/reqError";
import {
  CONSENT_LIST_PAGE_SIZE,
  createIncrementalConsentList,
  IncrementalConsentList,
  mergeIncrementalConsentPage,
} from "@source/api/models/consentHelpers";

type ConsentPageRequest<T, Q extends object> = (
  query: Q & {page: number; limit: number},
) => Promise<T[]>;

export interface IncrementalConsentListResult<T> extends IncrementalConsentList<T> {
  isLoading: boolean;
  error: string;
  loadMore: () => Promise<void>;
  retry: () => Promise<void>;
}

export default function useIncrementalConsentList<T, Q extends object>(
  request: ConsentPageRequest<T, Q>,
  filters: Q,
): IncrementalConsentListResult<T> {
  const [state, setState] = useState<IncrementalConsentList<T>>(
    createIncrementalConsentList<T>(),
  );
  const [stateFilterKey, setStateFilterKey] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorFilterKey, setErrorFilterKey] = useState("");
  const stateRef = useRef(state);
  const filtersRef = useRef(filters);
  const filterKeyRef = useRef("");
  const requestIdRef = useRef(0);
  const isLoadingRef = useRef(false);
  const errorRef = useRef("");
  stateRef.current = state;
  filtersRef.current = filters;
  errorRef.current = error;

  const filterKey = useMemo(() => JSON.stringify(filters), [filters]);
  filterKeyRef.current = filterKey;

  const loadPage = useCallback(async (page: number, reset: boolean) => {
    const requestId = ++requestIdRef.current;
    const requestFilterKey = filterKeyRef.current;
    isLoadingRef.current = true;
    setIsLoading(true);
    setError("");
    setErrorFilterKey("");
    try {
      const response = await request({...filtersRef.current, page, limit: CONSENT_LIST_PAGE_SIZE});
      if (requestId === requestIdRef.current && requestFilterKey === filterKeyRef.current) {
        setState((current) => mergeIncrementalConsentPage(current, response, reset));
        setStateFilterKey(requestFilterKey);
      }
    } catch (requestError: unknown) {
      if (requestId === requestIdRef.current && requestFilterKey === filterKeyRef.current) {
        if (reset) {
          setState(createIncrementalConsentList<T>());
          setStateFilterKey(requestFilterKey);
        }
        setError(
          requestError instanceof ReqError || requestError instanceof Error
            ? requestError.message
            : "Unable to load consent records.",
        );
        setErrorFilterKey(requestFilterKey);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    }
  }, [request, filterKey]);

  useEffect(() => {
    setState(createIncrementalConsentList<T>());
    loadPage(0, true);
  }, [loadPage]);

  const loadMore = useCallback(async () => {
    if (isLoadingRef.current || errorRef.current || !stateRef.current.hasMore) return;
    await loadPage(stateRef.current.page + 1, false);
  }, [loadPage]);

  const retry = useCallback(
    () => {
      const reset = stateRef.current.items.length === 0;
      return loadPage(reset ? 0 : stateRef.current.page + 1, reset);
    },
    [loadPage],
  );

  const visibleState = stateFilterKey === filterKey ? state : createIncrementalConsentList<T>();
  return {
    ...visibleState,
    isLoading: isLoading || stateFilterKey !== filterKey,
    error: errorFilterKey === filterKey ? error : "",
    loadMore,
    retry,
  };
}
